/** Edit dialogs for warehouse locations, expected stock lines and packages (cartons). */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { EditShell, F, inp, errMsg } from "./InvEditKit";

function useForm(init, dep) {
  const [f, setF] = useState(init);
  const [reason, setReason] = useState("");
  useEffect(() => { setF(init); setReason(""); }, [dep]); // eslint-disable-line react-hooks/exhaustive-deps
  return [f, setF, reason, setReason];
}

export function LocationEditDialog({ loc, onClose, onSaved }) {
  const [f, setF, reason, setReason] = useForm({ zone: loc?.zone || "", rack: loc?.rack || "", shelf: loc?.shelf || "", bin: loc?.bin || "", location_type: loc?.location_type || "STORAGE", max_devices: loc?.max_devices || "" }, loc?.id);
  const [busy, setBusy] = useState(false);
  if (!loc) return null;
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/inventory-admin/locations/${loc.location_id || loc.id}`, { ...f, max_devices: f.max_devices === "" ? 0 : Number(f.max_devices), reason }); toast.success(`Saved ${data.code}${data.units_relabelled ? ` · ${data.units_relabelled} unit(s) follow the new label` : ""}`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <EditShell open title={`Edit location ${loc.code}`} onClose={onClose} onSave={save} busy={busy} reason={reason} setReason={setReason} testid="loc-edit">
      {["zone", "rack", "shelf", "bin"].map((k) => <F key={k} l={k[0].toUpperCase() + k.slice(1)}><input value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value.toUpperCase() })} className={inp} data-testid={`loc-edit-${k}`} /></F>)}
      <F l="Type"><select value={f.location_type} onChange={(e) => setF({ ...f, location_type: e.target.value })} className={inp} data-testid="loc-edit-type">{["STORAGE", "STAGING", "QUARANTINE", "RECEIVING", "DISPATCH"].map((t) => <option key={t}>{t}</option>)}</select></F>
      <F l="Max devices"><input value={f.max_devices} onChange={(e) => setF({ ...f, max_devices: e.target.value.replace(/\D/g, "") })} className={inp} data-testid="loc-edit-max" /></F>
      <div className="col-span-2 text-[11px] text-slate-500">The LOCATION ID never changes. If the position changes, the label code changes, the units in it follow, and the old code keeps working as an alias.</div>
    </EditShell>
  );
}

export function ExpectedEditDialog({ line, onClose, onSaved }) {
  const [f, setF, reason, setReason] = useForm({ product_name: line?.product_name || "", part_number: line?.part_number || "", expected_qty: line?.expected_qty ?? "" }, line?.id);
  const [busy, setBusy] = useState(false);
  if (!line) return null;
  const save = async () => {
    setBusy(true);
    try { await api.put(`/inventory-admin/expected/${line.id}`, { ...f, expected_qty: Number(f.expected_qty), reason }); toast.success("Expected line updated"); onSaved(); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <EditShell open title="Edit expected stock line" onClose={onClose} onSave={save} busy={busy} reason={reason} setReason={setReason} testid="exp-edit">
      <F l="Product" wide><input value={f.product_name} onChange={(e) => setF({ ...f, product_name: e.target.value })} className={inp} data-testid="exp-edit-product" /></F>
      <F l="Part no."><input value={f.part_number} onChange={(e) => setF({ ...f, part_number: e.target.value })} className={inp} data-testid="exp-edit-part" /></F>
      <F l={`Expected qty (received ${Number(line.received_qty || 0)})`}><input value={f.expected_qty} onChange={(e) => setF({ ...f, expected_qty: e.target.value.replace(/[^\d.]/g, "") })} className={inp} data-testid="exp-edit-qty" /></F>
    </EditShell>
  );
}

export function PackageEditDialog({ pkg, onClose, onSaved }) {
  const [f, setF, reason, setReason] = useForm({ remove: [], add: "", note: pkg?.note || "" }, pkg?.id);
  const [busy, setBusy] = useState(false);
  if (!pkg) return null;
  const tog = (c) => setF({ ...f, remove: f.remove.includes(c) ? f.remove.filter((x) => x !== c) : [...f.remove, c] });
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put(`/inventory-admin/packages/${pkg.code}`, { reason, remove_codes: f.remove, add_codes: f.add.split(/[\s,]+/).filter(Boolean), note: f.note }); toast.success(`${data.code}: +${data.added.length} / −${data.removed.length} · ${data.qty} device(s)`); onSaved(); }
    catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <EditShell open title={`Edit carton ${pkg.code}`} onClose={onClose} onSave={save} busy={busy} reason={reason} setReason={setReason} testid="pkg-edit">
      <div className="col-span-2 space-y-1"><span className="text-[11px] text-slate-500">Contents · tick to remove</span>
        <div className="flex flex-wrap gap-1.5">{(pkg.inventory_codes || []).map((c) => <label key={c} className={`inline-flex items-center gap-1 rounded-md border px-2 py-0.5 font-mono text-[11px] ${f.remove.includes(c) ? "border-rose-300 bg-rose-50 line-through" : "border-slate-300"}`}><input type="checkbox" checked={f.remove.includes(c)} onChange={() => tog(c)} data-testid={`pkg-edit-remove-${c}`} />{c}</label>)}</div></div>
      <F l="Add units (scan / type inventory IDs or serials)" wide><input value={f.add} onChange={(e) => setF({ ...f, add: e.target.value })} className={`${inp} font-mono`} data-testid="pkg-edit-add" /></F>
      <F l="Note" wide><input value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className={inp} data-testid="pkg-edit-note" /></F>
    </EditShell>
  );
}
